import { TOCNode, TimepubCatalogNode } from '../types/DocumentTypes';
import { ProductMeta } from '@almamedia-legal-insights/types';

// ============================================================================
// Helper Functions
// ============================================================================

export function deserializeTOCNodes(nodes: TimepubCatalogNode[]): TOCNode[] {
  return nodes.map((node) => ({
    title: node.title,
    nodeId: node.id,
    parentNodeId: node.parentId,
    blockId: node.blockId,
    blockCount: node.blockCount,
    hasChildren: node.hasChildren,
    children: node.children ? deserializeTOCNodes(node.children) : null,
  }));
}

// ============================================================================
// API Functions
// ============================================================================

/**
 * Fetches product metadata from the backend API.
 * @param isbn - The ISBN of the product.
 * @param authToken - Bearer authentication token. If missing, relying on IP-authorization.
 * @returns A promise resolving to the ProductMeta.
 * @throws Error if the fetch fails.
 */
export async function fetchProductMeta(
  isbn: string,
  authToken?: string,
): Promise<ProductMeta> {
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  };

  if (authToken) {
    headers['Authorization'] = `Bearer ${authToken}`;
  }

  const apiUrl = `${import.meta.env.VITE_API_URL}/products/${isbn}`;

  try {
    const response = await fetch(apiUrl, { method: 'GET', headers });

    if (!response.ok) {
      const errorBody = await response
        .text()
        .catch(() => 'Could not read error body');
      throw new Error(
        `API Error fetching product meta for ISBN ${isbn}: ${response.status} ${response.statusText} - ${errorBody}`,
      );
    }

    const json = await response.json();
    return json as ProductMeta;
  } catch (error) {
    console.error(`fetchProductMeta failed for ISBN ${isbn}:`, error);
    throw error;
  }
}

/**
 * Fetches the Table of Contents (TOC) for a given document ID from the backend API.
 * Requires an authentication token.
 *
 * --- CURRENTLY RETURNS MOCK DATA ---
 * Remove mock implementation when API endpoint is ready.
 *
 * @param id - The document identifier (ISBN).
 * @param authToken - Bearer authentication token.
 * @returns A promise resolving to an array of TOCNode objects.
 * @throws Error if the fetch fails or auth token is missing.
 */
export async function fetchDocumentTOC(
  id: string,
  authToken?: string,
): Promise<TOCNode[]> {
  const apiUrl = `${import.meta.env.VITE_API_URL}/books/${id}/toc?childDepth=2`; // Example endpoint
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...(authToken && {
      Authorization: `Bearer ${authToken}`,
    }),
  };

  try {
    const response = await fetch(apiUrl, {
      headers,
      method: 'GET',
    });

    if (!response.ok) {
      const errorBody = await response
        .text()
        .catch(() => 'Could not read error body');
      throw new Error(
        `API Error fetching TOC for ID ${id}: ${response.status} ${response.statusText} - ${errorBody}`,
      );
    }

    const json = (await response.json()) as { results: TimepubCatalogNode[] };

    // Assuming the API returns data in the TimepubCatalogNode structure directly under a 'data' key or similar
    const rawNodes = json.results; // Adjust based on actual API response

    if (!Array.isArray(rawNodes)) {
      throw new Error(
        'fetchDocumentTOC: Invalid API response format, expected array.',
      );
    }

    return deserializeTOCNodes(rawNodes);
  } catch (error) {
    console.error(`fetchDocumentTOC failed for ID ${id}:`, error);
    throw error;
  }
}

export async function fetchChapterByPageNumber(
  id: string,
  pageNumber: string,
  authToken?: string,
): Promise<TOCNode[]> {
  const apiUrl = `${import.meta.env.VITE_API_URL}/books/${id}/position`; // Example endpoint
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...(authToken && {
      Authorization: `Bearer ${authToken}`,
    }),
  };

  try {
    const response = await fetch(apiUrl, {
      headers,
      body: JSON.stringify({ pageNumber }),
      method: 'POST',
    });

    if (!response.ok) {
      throw new Error(`API Error fetching chapter by page number`);
    }

    const json = (await response.json()) as {
      crumbTrail: TimepubCatalogNode[];
      pageNumber: string;
    };

    // Assuming the API returns data in the TimepubCatalogNode structure directly under a 'data' key or similar
    const nodes = json.crumbTrail; // Adjust based on actual API response

    const deserializedNodes = deserializeTOCNodes(nodes);

    if (deserializedNodes.length === 0) {
      throw new Error('No chapter found by page number');
    }

    return deserializedNodes;
  } catch (error) {
    console.error(`Fetching chapter by page number failed:`, error);
    throw error;
  }
}
